import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { PrimaryCTA } from './PrimaryCTA';
import { api } from '../api/remembrApi';
import type {
  InventoryItemResponse,
  UpdateInventoryItemRequest,
} from '../api/types';
import { fpColors } from '../styles/theme';
import { useAppSelector } from '../store/store';
import {
  selectAllTags,
  selectCategories,
  selectAllLocations,
} from '../features/browse/browseSlice';
import { SuggestionField } from './SuggestionField';
import { TagsField } from './TagsField';

/**
 * Bottom-sheet-ish modal used by the scan/verify flow to edit an
 * AI-detected item before confirming it.
 *
 * Fields exposed: item_name, item_category, item_description, location,
 * estimated_value — mapping to UpdateInventoryItemRequest. Saving calls
 * api.updateInventoryItem(id, patch) and returns the updated item via
 * onSaved(). If the item has no id (shouldn't happen for detected items,
 * but guards for safety) Save performs a client-side merge only.
 */
interface EditItemSheetProps {
  visible: boolean;
  item: InventoryItemResponse | null;
  onClose: () => void;
  onSaved: (updated: InventoryItemResponse) => void;
}

export const EditItemSheet: React.FC<EditItemSheetProps> = ({
  visible,
  item,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search-UX §3.11 — suggestion pools from the same selectors the
  // Browse Tag + Category surfaces use, so the picker offers the
  // user's actual existing values (kills duplicate-tag /
  // duplicate-category fragmentation at the source).
  const allTags = useAppSelector(selectAllTags);
  const allCategories = useAppSelector(selectCategories);
  const allLocations = useAppSelector(selectAllLocations);
  const tagsPool = allTags;
  const categoriesPool = React.useMemo(
    () => allCategories.map((c) => ({ label: c })),
    [allCategories],
  );
  const locationsPool = React.useMemo(
    () => allLocations.map((l) => ({ label: l.location, meta: `${l.count} items` })),
    [allLocations],
  );

  useEffect(() => {
    if (!visible || !item) return;
    setName(item.item_name ?? '');
    setCategory(item.item_category ?? '');
    setLocation(item.location ?? '');
    setEstimatedValue(
      item.estimated_value !== undefined && item.estimated_value !== null
        ? String(item.estimated_value)
        : '',
    );
    setDescription(item.item_description ?? '');
    // Tags arrive as a comma-separated string from the backend.
    // Render verbatim; user adds/removes whatever they want.
    setTags(item.tags ?? '');
    setError(null);
  }, [visible, item]);

  // Normalize the tags string before sending: split on commas,
  // trim each piece, drop empties, dedupe (case-insensitive),
  // re-join. Backend stores a single string but downstream search
  // splits on comma — keeping this hygiene prevents `,,,` and
  // duplicate-with-leading-space ghosts.
  const normalizeTags = (raw: string): string | undefined => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const piece of raw.split(',')) {
      const t = piece.trim();
      if (!t) continue;
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
    return out.length ? out.join(', ') : undefined;
  };

  const handleSave = async () => {
    if (!item) return;
    setError(null);
    const normalizedTags = normalizeTags(tags);
    const patch: UpdateInventoryItemRequest = {
      item_name: name.trim() || undefined,
      item_category: category.trim() || undefined,
      location: location.trim() || undefined,
      item_description: description.trim() || undefined,
      tags: normalizedTags,
    };
    if (estimatedValue.trim()) {
      const parsed = Number(estimatedValue.replace(/[^0-9.]/g, ''));
      if (!Number.isNaN(parsed)) patch.estimated_value = parsed;
    }

    const merged: InventoryItemResponse = {
      ...item,
      item_name: patch.item_name ?? item.item_name,
      item_category: patch.item_category ?? item.item_category,
      location: patch.location ?? item.location,
      item_description: patch.item_description ?? item.item_description,
      estimated_value: patch.estimated_value ?? item.estimated_value,
      tags: normalizedTags ?? item.tags,
    };

    // If we don't have a real ID we can't persist — just close with the
    // merged local copy. (Shouldn't happen in the normal scan flow.)
    if (!item.id) {
      onSaved(merged);
      onClose();
      return;
    }

    setSaving(true);
    try {
      const updated = await api.updateInventoryItem(item.id, patch);
      onSaved(updated);
      onClose();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('✏️ EditItemSheet: updateInventoryItem failed', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      const detailRaw = err?.response?.data?.detail;
      const detail = Array.isArray(detailRaw)
        ? detailRaw.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : typeof detailRaw === 'string'
          ? detailRaw
          : null;
      setError(detail || err?.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.scrim}>
        <Pressable style={styles.scrimClick} onPress={onClose} />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.sheetWrap}
        >
          <View style={styles.sheet}>
            <View style={styles.handle} />
            <View style={styles.header}>
              <Text style={styles.title}>Edit item</Text>
              <Pressable onPress={onClose} hitSlop={8} style={styles.closeBtn}>
                <MaterialIcons name="close" size={20} color={fpColors.onSurface} />
              </Pressable>
            </View>

            <ScrollView
              style={styles.form}
              contentContainerStyle={styles.formInner}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              <Field
                label="Item name"
                value={name}
                onChangeText={setName}
                placeholder="What is it?"
                autoCapitalize="sentences"
              />
              <SuggestionField
                label="Category"
                value={category}
                onChangeText={setCategory}
                placeholder="e.g. electronics, kitchenware"
                suggestions={categoriesPool}
                hint="Pick an existing category or type to create a new one."
                testID="edit-item-category"
              />
              <SuggestionField
                label="Location"
                value={location}
                onChangeText={setLocation}
                placeholder="Where is it stored?"
                autoCapitalize="sentences"
                suggestions={locationsPool}
                hint="Pick an existing location or type to create a new one."
                testID="edit-item-location"
              />
              <Field
                label="Estimated value"
                value={estimatedValue}
                onChangeText={setEstimatedValue}
                placeholder="$0"
                keyboardType="decimal-pad"
              />
              <Field
                label="Description"
                value={description}
                onChangeText={setDescription}
                placeholder="Add details, brand, notes…"
                multiline
                numberOfLines={3}
              />
              <TagsField
                label="Tags"
                value={tags}
                onChange={setTags}
                placeholder="Add tag"
                suggestions={tagsPool}
                testID="edit-item-tags"
              />

              {error ? (
                <View style={styles.errorBanner}>
                  <MaterialIcons
                    name="error-outline"
                    size={16}
                    color={fpColors.error}
                  />
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              ) : null}
            </ScrollView>

            <View style={styles.footer}>
              <Pressable
                onPress={onClose}
                style={({ pressed }) => [
                  styles.cancelBtn,
                  pressed && styles.pressed,
                ]}
              >
                <Text style={styles.cancelLabel}>Cancel</Text>
              </Pressable>
              <View style={{ flex: 2 }}>
                <PrimaryCTA
                  label={saving ? 'Saving…' : 'Save changes'}
                  onPress={handleSave}
                  loading={saving}
                  iconRight={saving ? null : 'check-circle'}
                />
              </View>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChangeText: (t: string) => void;
  placeholder?: string;
  multiline?: boolean;
  numberOfLines?: number;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  keyboardType?: 'default' | 'decimal-pad' | 'numeric';
  hint?: string;
}> = ({
  label,
  value,
  onChangeText,
  placeholder,
  multiline,
  numberOfLines,
  autoCapitalize,
  keyboardType,
  hint,
}) => (
  <View style={styles.field}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={`${fpColors.outline}99`}
      multiline={multiline}
      numberOfLines={numberOfLines}
      autoCapitalize={autoCapitalize}
      keyboardType={keyboardType}
      style={[styles.input, multiline && styles.inputMulti]}
    />
    {hint ? <Text style={styles.fieldHint}>{hint}</Text> : null}
  </View>
);

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(31, 26, 34, 0.45)',
    justifyContent: 'flex-end',
  },
  scrimClick: { flex: 1 },
  sheetWrap: { justifyContent: 'flex-end' },
  sheet: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
    maxHeight: '92%',
  },
  handle: {
    alignSelf: 'center',
    marginTop: 8,
    width: 44,
    height: 4,
    borderRadius: 2,
    backgroundColor: fpColors.outlineVariant,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: `${fpColors.outlineVariant}80`,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 17,
    color: fpColors.onSurface,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  form: { maxHeight: 520 },
  formInner: {
    padding: 20,
    gap: 14,
  },
  field: { gap: 6 },
  fieldLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  fieldHint: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
    marginTop: 2,
  },
  input: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  inputMulti: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onErrorContainer,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 56,
  },
  cancelLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  pressed: { opacity: 0.85 },
});
